import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useLocation, useHistory } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroller';
import { message } from 'antd';
import classNames from 'classnames';
import styles from './SearchWallpaper.module.less';

import { CONTEXT } from '../../config';
import Loading from '../../components/Loading/Loading';
import { getQueryString } from '../../utils/utils';
import Toast from '../../components/Toast/Toast';
import Dialog from '../../components/Dialog/Dialog';
import WallpaperCard from '../../components/WallpaperCard/WallpaperCard';

import { getSearchWallpaper, getSearchWallpaperHot } from '../../actions/searchResult';
import { logging, logPostInfo } from '../../actions/log';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectSearchInfo = createSelector(
  state => state.search,
  search => search,
);
const selectSearchKw = createSelector(
  state => state.layout,
  layout => layout.searchKw,
);

const selectSearchResultObj = createSelector(
  state => state.searchResult,
  searchResult => searchResult,
);

export default memo(({ scrollNode, logInfo }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const logInfoData = useSelector(selectLogInfo);
  const { searchParam, searchWallpaper, searchCount, searchWallpaperHot } = useSelector(
    selectSearchResultObj,
  );

  const [showNumber, setShowNumber] = useState(8);

  const keyword = getQueryString('keyword');

  // 浏览时长打桩
  const leaveTimeLog = useCallback(
    initTime => {
      const keyword = getQueryString('keyword');
      const leaveTime = +new Date();
      dispatch(
        logging({
          category: 'I',
          action: 'View_Time',
          label: '',
          param2: {
            source: `Search_Wallpaper`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            search_type: logInfoData.search_type,
            search_word: logInfoData.search_word || keyword,
            initTime,
            leaveTime,
            time: leaveTime - initTime,
            keyword,
            searchParam,
          },
          param3: searchWallpaper.bizInfo,
        }),
      );
    },
    [logInfoData, searchParam, searchWallpaper],
  );
  // 统计页面浏览时长
  useEffect(() => {
    const initTime = +new Date();
    return () => {
      leaveTimeLog(initTime);
    };
  }, []);

  /**
   * 当数据为activeTab数据，tab为activeTab，且请求的是第一页的数据
   * 上报请求完成的桩，当做一次搜索结果成功的上报
   */
  useEffect(() => {
    if (
      searchParam?.searchWord === searchWallpaper?.searchParam?.searchWord &&
      searchParam?.activeTab === 'wallpaper' &&
      searchParam?.wallpaperPaySel === searchWallpaper?.searchParam?.wallpaperPaySel &&
      searchWallpaper?.loading === 200
    ) {
      // 请求数据成功
      if (searchWallpaper?.dataList?.length > 0 && searchWallpaper?.dataList?.length <= 48) {
        dispatch(
          logging({
            category: 'R',
            action: 'SearchResult',
            label: '',
            param2: {
              source: 'Search_Wallpaper',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              has_content: searchWallpaper?.dataList?.length > 0 ? 1 : 0,
              search_type: logInfoData.search_type,
              search_word: logInfoData.search_word || keyword,
              keyword,
            },
            param3: searchWallpaper?.bizInfo,
          }),
        );
      }
      // 请求数据为空
      if (searchWallpaper?.dataList?.length === 0) {
        dispatch(
          logging({
            category: 'R',
            action: 'SearchResult_NoData',
            label: '',
            param2: {
              source: 'Search_Wallpaper',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              has_content: searchWallpaper?.dataList?.length > 0 ? 1 : 0,
              search_type: logInfoData.search_type,
              search_word: logInfoData.search_word || keyword,
              keyword,
            },
            param3: searchWallpaper?.bizInfo,
          }),
        );
      }
    }
  }, [searchWallpaper, searchParam]);

  // 获取推荐内容
  useEffect(() => {
    dispatch(getSearchWallpaperHot());
  }, []);

  // 当searchParam参数和  返回的数据中pageParam参数不一致的时候，就重新请求页面数据
  useEffect(() => {
    if (
      !(
        searchParam?.searchWord === searchWallpaper?.searchParam?.searchWord &&
        searchParam?.activeTab === searchWallpaper?.searchParam?.activeTab &&
        searchParam?.wallpaperPaySel === searchWallpaper?.searchParam?.wallpaperPaySel
      )
    ) {
      let params = {
        refresh: true,
        searchWord: getQueryString('keyword'),
        wallpaperPaySel: searchParam?.wallpaperPaySel || '0',
        algoBizInfo: searchWallpaper?.algoBizInfo || '',
      };
      // 如果切换搜索词
      if (searchParam?.searchWord !== searchWallpaper?.searchParam?.searchWord) {
        params.algoBizInfo = '';
      }

      // 传入相关数据（重新）请求数据
      dispatch(getSearchWallpaper(params));
    }
  }, [searchWallpaper, searchParam]);

  const loadMore = useCallback(() => {
    let params = {
      searchMode: '1', // searchMode仅仅在loadmore时传入  因为useEffect并不知道是否为点击搜索按钮的搜索
      refresh: false,
      searchWord: getQueryString('keyword'),
      wallpaperPaySel: searchParam?.wallpaperPaySel || '0',
      algoBizInfo: searchWallpaper?.algoBizInfo || '',
    };
    // 传入相关数据（继续）请求数据
    dispatch(getSearchWallpaper(params));
  }, [searchParam, searchWallpaper]);

  // 监听浏览器大小变化自动适应
  useEffect(() => {
    window.addEventListener('resize', handleResize, false);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleResize = useCallback(e => {
    const clientWidth = (document.body && document.body.clientWidth) || 1060;
    if (clientWidth < 1330) {
      setShowNumber(6);
    } else if (clientWidth >= 1330 && clientWidth < 1600) {
      setShowNumber(8);
    } else if (clientWidth >= 1600 && clientWidth < 1870) {
      setShowNumber(10);
    } else if (clientWidth >= 1870) {
      setShowNumber(12);
    }
  }, []);

  const getClassType = useCallback((index = 0, number = 6) => {
    const series = {
      '6': {
        small: [1, 4],
        small_space: [2, 3, 5, 0],
      },
      '8': {
        small: [1, 5],
        small_space: [2, 3, 4, 6, 7, 0],
      },
      '10': {
        small: [1, 6],
        small_space: [2, 3, 4, 5, 7, 8, 9, 0],
      },
      '12': {
        small: [1, 7],
        small_space: [2, 3, 4, 5, 6, 8, 9, 10, 11, 0],
      },
    };
    const pos = (index + 1) % parseInt(number);
    let classType = 'small';
    if (series[number].small.indexOf(pos) > -1) {
      classType = 'small';
    }
    if (series[number].small_space.indexOf(pos) > -1) {
      classType = 'small_space';
    }
    return classType;
  }, []);

  const handleSearchOut = () => {
    window.open(`https://www.baidu.com/s?wd=${keyword}`);
    dispatch(
      logging({
        category: 'C',
        action: 'Search_To_Browser',
        label: '',
        param2: {
          source: 'Search_Wallpaper',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          search_type: logInfoData.search_type,
          search_word: logInfoData.search_word || keyword,
          keyword,
        },
      }),
    );
  };

  if (
    !searchWallpaper ||
    !searchWallpaper?.loading ||
    searchWallpaper?.loading !== 200 ||
    searchWallpaper?.searchParam?.searchWord !== getQueryString('keyword')
  ) {
    return (
      <div className={styles.s_loading}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.s_wallpaper}>
      <InfiniteScroll
        pageStart={1}
        loadMore={loadMore}
        hasMore={searchWallpaper?.dataList?.length < searchCount?.wallpapersCount}
        loader={
          <div className="loader" key={0}>
            ----------加载中----------
          </div>
        }
        initialLoad={false}
        useWindow={false}
        getScrollParent={() => scrollNode}
        threshold={800}
      >
        {searchWallpaper?.dataList?.length > 0 && (
          <div className={'s_list'}>
            {searchWallpaper?.dataList.map((item, index) => {
              return (
                <WallpaperCard
                  wallpaper={item}
                  key={index}
                  cardInfo={{
                    allData: searchWallpaper?.dataList,
                    isNoNeedInView: true,
                    pageSource: 'Search_Wallpaper',
                    pageTage: '',
                    classType: getClassType(index, showNumber),
                    isCollect: true,
                    isWallpaper: true,
                    isDeleteColl: false,
                    isDeleteUsed: false,
                    isPreview: true,
                  }}
                  logInfo={{ source: 'Search_Wallpaper' }}
                />
              );
            })}
          </div>
        )}

        {searchWallpaper?.dataList?.length > 0 &&
          searchWallpaper?.dataList?.length >= searchCount?.wallpapersCount && (
            <div className={styles.search_out} onClick={handleSearchOut}>
              没找到满意结果？试试{' '}
              <span className={styles.search_out_text}>
                百度搜索 &quot;{keyword}&quot;&nbsp;&gt;
              </span>
            </div>
          )}

        {searchWallpaper?.dataList?.length === 0 && (
          <div className={styles.Nosearchresult}>
            <div className="NosearchresultBox">
              <div className="noresultImg" />
              <div className="mainTextWrap">
                <div className="mainText" onClick={handleSearchOut}>
                  没找到匹配结果。试试&nbsp;
                  <span className={styles.search_out_text}>
                    百度搜索 &quot;{keyword}&quot;&nbsp;&gt;
                  </span>
                  <br />
                </div>
              </div>
            </div>
          </div>
        )}
        {((searchWallpaper?.dataList?.length > 0 &&
          searchWallpaper?.dataList?.length >= searchCount?.wallpapersCount) ||
          !searchWallpaper?.dataList?.length ||
          searchWallpaper?.dataList?.length === 0) &&
          searchWallpaperHot?.wallpapers?.length > 0 && (
            <div className={styles.s_reco}>
              <div className={'s_reco_header'}>
                <div className={'s_reco_header_title'}>看看这些最新壁纸</div>
                <div
                  className={'s_reco_header_more'}
                  onClick={() => {
                    dispatch(
                      logging({
                        category: 'C',
                        action: 'Search_Wallpaper_Reco_More',
                        label: '',
                        param2: {
                          source: 'Search_Wallpaper',
                          source_p: logInfoData.source_p,
                          source_pp: logInfoData.source_pp,
                          search_type: logInfoData.search_type,
                          search_word: logInfoData.search_word || keyword,
                          keyword,
                        },
                      }),
                    );
                    dispatch(
                      logPostInfo({
                        source: 'Search_Wallpaper_Reco',
                        source_p: logInfoData.source_p,
                        source_pp: logInfoData.source_pp,
                      }),
                    );
                    history.push(`${CONTEXT}/wallpaper/wpcategory`);
                  }}
                >
                  查看更多
                </div>
              </div>
              <div className={'s_reco_list'}>
                {searchWallpaperHot?.wallpapers?.slice(0, showNumber * 2).map((item, index) => {
                  return (
                    <WallpaperCard
                      wallpaper={item}
                      key={index}
                      cardInfo={{
                        allData: searchWallpaperHot?.wallpapers,
                        isNoNeedInView: true,
                        pageSource: 'Search_Wallpaper_Reco',
                        pageTage: '',
                        classType: getClassType(index, showNumber),
                        isCollect: true,
                        isWallpaper: true,
                        isDeleteColl: false,
                        isDeleteUsed: false,
                        isPreview: true,
                      }}
                      logInfo={{ source: 'Search_Wallpaper_Reco' }}
                    />
                  );
                })}
              </div>
            </div>
          )}
      </InfiniteScroll>
    </div>
  );
});
